import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authzApi } from '../../api/authz';

type Condition = { field: string; operator: string; value: string };

export function PolicyBuilderPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [effect, setEffect] = useState<'allow' | 'deny'>('allow');
  const [priority, setPriority] = useState(10);
  const [resource, setResource] = useState('*');
  const [action, setAction] = useState('*');
  const [tenantId, setTenantId] = useState('');
  const [conditions, setConditions] = useState<Condition[]>([{ field: 'tenant_id', operator: 'eq', value: '' }]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  function updateCondition(i: number, patch: Partial<Condition>) {
    setConditions((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await authzApi.createPolicy({
        name,
        description,
        effect,
        priority,
        resource,
        action,
        tenant_id: tenantId || undefined,
        enabled: true,
        conditions: conditions.filter((c) => c.field && c.value),
      });
      navigate('/authorization/policies');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create policy');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl p-6">
      <Link to="/authorization" className="text-sm text-slate-600 hover:underline">← Dashboard</Link>
      <h1 className="mt-4 text-2xl font-semibold text-slate-900">Policy builder</h1>
      <p className="mt-1 text-sm text-slate-600">Define ABAC policies with attribute conditions.</p>
      {error && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      <form onSubmit={onSubmit} className="mt-6 space-y-4 rounded-xl border border-slate-200 bg-white p-4">
        <label className="block text-sm text-slate-600">Name
          <input required value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" />
        </label>
        <label className="block text-sm text-slate-600">Description
          <input value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" />
        </label>
        <div className="grid gap-3 md:grid-cols-2">
          <label className="block text-sm text-slate-600">Effect
            <select value={effect} onChange={(e) => setEffect(e.target.value as 'allow' | 'deny')} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2">
              <option value="allow">Allow</option>
              <option value="deny">Deny</option>
            </select>
          </label>
          <label className="block text-sm text-slate-600">Priority
            <input type="number" value={priority} onChange={(e) => setPriority(Number(e.target.value))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" />
          </label>
          <label className="block text-sm text-slate-600">Resource
            <input value={resource} onChange={(e) => setResource(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" />
          </label>
          <label className="block text-sm text-slate-600">Action
            <input value={action} onChange={(e) => setAction(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" />
          </label>
        </div>
        <label className="block text-sm text-slate-600">Tenant scope (optional)
          <input value={tenantId} onChange={(e) => setTenantId(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" />
        </label>
        <fieldset className="rounded-lg border border-slate-200 p-3">
          <legend className="px-1 text-sm font-medium text-slate-700">Conditions</legend>
          {conditions.map((c, i) => (
            <div key={i} className="mt-2 grid gap-2 md:grid-cols-3">
              <input placeholder="Field" value={c.field} onChange={(e) => updateCondition(i, { field: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2" />
              <select value={c.operator} onChange={(e) => updateCondition(i, { operator: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2">
                <option value="eq">equals</option>
                <option value="ne">not equals</option>
                <option value="in">in</option>
                <option value="contains">contains</option>
              </select>
              <input placeholder="Value" value={c.value} onChange={(e) => updateCondition(i, { value: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2" />
            </div>
          ))}
          <button type="button" onClick={() => setConditions((p) => [...p, { field: '', operator: 'eq', value: '' }])} className="mt-2 text-sm text-blue-600 hover:underline">
            Add condition
          </button>
        </fieldset>
        <button type="submit" disabled={loading} className="rounded-lg bg-slate-900 px-4 py-2 text-white disabled:opacity-50">
          {loading ? 'Creating…' : 'Create policy'}
        </button>
      </form>
    </div>
  );
}
